import React from "react";
import { X } from "lucide-react";

// Import all accessory SVGs
import cleanPaw from "../../assets/Materials/Accessories/clean_paw.svg";
import superCleanPaw from "../../assets/Materials/Accessories/super_clean_paw.svg";
import dirtyPaw from "../../assets/Materials/Accessories/dirty_paw.svg";
import bonePaw from "../../assets/Materials/Accessories/bone_paw.svg";
import pawAndClawPixel from "../../assets/Materials/Accessories/paw_and_claw_pixel.svg";
import pixelPaws from "../../assets/Materials/Accessories/pixel_paws.svg";
import pixelHeart from "../../assets/Materials/Accessories/pixel_heart.svg";
import simpleHeart from "../../assets/Materials/Accessories/simple_heart.svg";
import heartNecklace from "../../assets/Materials/Accessories/heart_shape_necklace.svg";
import dogTag from "../../assets/Materials/Accessories/dog_tag.svg";

export const ACCESSORIES = [
  { id: "clean_paw",          name: "Clean Paw",      src: cleanPaw,         category: "paw" },
  { id: "super_clean_paw",    name: "Neat Paw",       src: superCleanPaw,    category: "paw" },
  { id: "dirty_paw",          name: "Muddy Paw",      src: dirtyPaw,         category: "paw" },
  { id: "bone_paw",           name: "Bone + Paw",     src: bonePaw,          category: "paw" },
  { id: "paw_and_claw_pixel", name: "Pixel Paw+Claw", src: pawAndClawPixel,  category: "paw" },
  { id: "pixel_paws",         name: "Pixel Paws",     src: pixelPaws,        category: "paw" },
  { id: "pixel_heart",        name: "Pixel Heart",    src: pixelHeart,       category: "heart" },
  { id: "simple_heart",       name: "Simple Heart",   src: simpleHeart,      category: "heart" },
  { id: "heart_necklace",     name: "Heart Necklace", src: heartNecklace,    category: "heart" },
  { id: "dog_tag",            name: "Dog Tag",        src: dogTag,           category: "misc" },
];

// A side with all 3 lines filled has no free space left for a decoration —
// the auto-placement zones (top/bottom/corners) assume at least one line is
// free to place around.
const MAX_LINES_WITH_ROOM = 2;

const countFilledLines = (config) =>
  [config?.line1, config?.line2, config?.line3].filter(Boolean).length;

/**
 * AccessoryPicker
 *
 * Props:
 *   side1Accessory  — { id, zone } | null  — what's assigned to side 1
 *   side2Accessory  — { id, zone } | null  — what's assigned to side 2
 *   side1Config     — { line1, line2, line3, ... } — Side 1's engraved text
 *   side2Config     — { line1, line2, line3, ... } — Side 2's engraved text
 *   designingSide   — 1 | 2               — which side the picker is acting on
 *   onChange(side, accessoryOrNull)        — callback when selection changes
 */
export default function AccessoryPicker({
  side1Accessory,
  side2Accessory,
  side1Config,
  side2Config,
  designingSide,
  onChange,
}) {
  const currentAccessory = designingSide === 1 ? side1Accessory : side2Accessory;
  const otherSideAccessory = designingSide === 1 ? side2Accessory : side1Accessory;
  const currentSideConfig = designingSide === 1 ? side1Config : side2Config;
  const currentSideLineCount = countFilledLines(currentSideConfig);
  const hasRoom = currentSideLineCount <= MAX_LINES_WITH_ROOM;

  const handleSelect = (acc) => {
    if (currentAccessory?.id === acc.id) {
      // clicking active selection clears it
      onChange(designingSide, null);
    } else {
      onChange(designingSide, { id: acc.id, zone: "auto" });
    }
  };

  return (
    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-semibold text-light-text dark:text-dark-text">
            Add Decoration — Side {designingSide}
          </p>
          <p className="text-xs text-light-textMuted dark:text-dark-textMuted mt-0.5">
            Auto-placed around your text. One per side.
          </p>
        </div>
        {currentAccessory && (
          <button
            type="button"
            onClick={() => onChange(designingSide, null)}
            className="text-xs flex items-center gap-1 text-amber-700 dark:text-amber-300 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {!hasRoom && !currentAccessory && (
        <p className="text-xs text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700 rounded-md px-3 py-2 mb-3">
          Side {designingSide} already has 3 lines of text — there's no room left to add a decoration without crowding it. Remove a line on Side {designingSide} to free up space.
        </p>
      )}
      {!hasRoom && currentAccessory && (
        <p className="text-xs text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700 rounded-md px-3 py-2 mb-3">
          Side {designingSide} now has 3 lines of text, so this decoration no longer fits cleanly — clear it or remove a line to keep the layout tidy.
        </p>
      )}

      <div className={`grid grid-cols-5 gap-2 ${!hasRoom ? "opacity-50" : ""}`}>
        {ACCESSORIES.map((acc) => {
          const isThisSide = currentAccessory?.id === acc.id;
          const isOtherSide = otherSideAccessory?.id === acc.id;
          const isSelectable = !isOtherSide && (hasRoom || isThisSide);

          return (
            <button
              key={acc.id}
              type="button"
              title={
                !hasRoom && !isThisSide
                  ? `Side ${designingSide} is full (3 lines of text) — no room for a decoration`
                  : acc.name
              }
              onClick={() => isSelectable && handleSelect(acc)}
              disabled={!isSelectable}
              className={`
                relative p-2 rounded-lg border-2 transition-all
                ${isThisSide
                  ? "border-amber-500 bg-amber-100 dark:bg-amber-900/50 shadow-md"
                  : isOtherSide
                  ? "border-gray-200 dark:border-gray-700 opacity-35 cursor-not-allowed"
                  : !hasRoom
                  ? "border-gray-200 dark:border-gray-700 cursor-not-allowed"
                  : "border-light-border dark:border-dark-border bg-white dark:bg-dark-surfaceLight hover:border-amber-400 dark:hover:border-amber-500 cursor-pointer"
                }
              `}
            >
              <img
                src={acc.src}
                alt={acc.name}
                // Same black-in-light / white-in-dark treatment as the
                // engraved preview icons, so they stay visible against the
                // dark-mode button background too.
                className="w-8 h-8 object-contain mx-auto drop-shadow-[0_1px_2px_rgba(0,0,0,0.2)] dark:brightness-0 dark:invert"
              />

              {/* Badge: which side this is assigned to */}
              {isThisSide && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-amber-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold leading-none">
                  {designingSide}
                </span>
              )}
              {isOtherSide && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-gray-400 text-white text-[9px] rounded-full flex items-center justify-center font-bold leading-none">
                  {designingSide === 1 ? 2 : 1}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {currentAccessory && (
        <p className="text-xs text-amber-700 dark:text-amber-300 mt-2 font-medium">
          ✓ {ACCESSORIES.find((a) => a.id === currentAccessory.id)?.name} on Side {designingSide}
          {" "}— placed to avoid your text
        </p>
      )}
    </div>
  );
}
